// The page side of director mode (director.ts): what the browser would otherwise move on its own.
//
//   CSS transitions / keyframes  ── held (paused) and moved by `tick(dt)`, so the HUD follows the steps
//   performance.now()            ── virtual: the labels' and ribbons' "settled after N ms" timers
//                                   count stepped time, not the time the recorder took
//   #director-caption            ── a fixed overlay the recorder sets through `caption(text)`

import type { DirectorPage } from './director.ts';

/** Id of the caption element, for a recorder that styles or reads it directly. */
export const CAPTION_ID = 'director-caption';

const MS_PER_S = 1000;

/** The browser services the page side needs, injectable so tests need no DOM. */
export interface PageEnv {
  document: Document;
  perf: Performance;
  /** Run `cb` before the next paint. */
  frame(cb: () => void): void;
  /** Resolve after one macrotask. */
  task(): Promise<void>;
}

// A fixed look, so every recording captions the same way. Bottom centre, above the HUD, never
// takes the pointer. The fade is a CSS transition: it follows the stepped clock like the HUD's.
const CAPTION_CSS = `
#${CAPTION_ID} {
  position: fixed;
  left: 50%;
  bottom: 14vh;
  transform: translateX(-50%);
  max-width: 70vw;
  padding: 0.45em 1.1em;
  box-sizing: border-box;
  font: 600 clamp(20px, 2.4vw, 44px) / 1.25 Georgia, 'Times New Roman', serif;
  color: #f6ecd0;
  text-align: center;
  background: rgba(12, 16, 28, 0.78);
  border: 1px solid rgba(214, 180, 106, 0.65);
  border-radius: 6px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  pointer-events: none;
  z-index: 2147483000;
  opacity: 0;
  transition: opacity 0.35s ease;
}
#${CAPTION_ID}[data-visible='true'] {
  opacity: 1;
}
`;

function browserEnv(): PageEnv {
  return {
    document,
    perf: performance,
    frame: (cb) => void requestAnimationFrame(cb),
    task: () => new Promise((resolve) => setTimeout(resolve)),
  };
}

/** Total length of an animation (delay included); Infinity for a looping one. */
function lengthOf(a: Animation): number {
  return Number(a.effect?.getComputedTiming().endTime ?? 0);
}

export function createPage(env: PageEnv = browserEnv()): DirectorPage {
  const { document: doc, perf } = env;
  const held = new WeakSet<Animation>();

  // Virtual time: starts where the real clock is (so "now - then" stays positive for code that
  // stamped itself before), then moves only by tick().
  let now = perf.now();
  perf.now = () => now;

  /**
   * Take charge of every animation not seen before: pause it at its start. One that began in real
   * time before this look restarts from zero, so a run does not depend on how long the recorder took.
   */
  function hold(): void {
    for (const a of doc.getAnimations()) {
      if (held.has(a)) {
        continue;
      }
      held.add(a);
      a.pause();
      a.currentTime = 0;
    }
  }

  // New animations are held at the next paint, before they can play a frame of real time.
  function guard(): void {
    hold();
    env.frame(guard);
  }
  env.frame(guard);

  const style = doc.createElement('style');
  style.textContent = CAPTION_CSS;
  doc.head.append(style);

  const caption = doc.createElement('div');
  caption.id = CAPTION_ID;
  caption.dataset.visible = 'false';
  caption.setAttribute('aria-live', 'polite');
  doc.body.append(caption);

  return {
    tick(dt) {
      const ms = dt * MS_PER_S;
      now += ms;
      hold();
      for (const a of doc.getAnimations()) {
        const next = Number(a.currentTime ?? 0) + ms;
        if (next >= lengthOf(a)) {
          a.finish();
          continue;
        }
        a.currentTime = next;
      }
    },

    finish() {
      hold();
      for (const a of doc.getAnimations()) {
        if (Number.isFinite(lengthOf(a))) {
          a.finish();
        }
      }
    },

    async flush() {
      await new Promise<void>((resolve) => env.frame(resolve));
      await env.task();
    },

    idle: () => env.task(),

    caption(text) {
      caption.textContent = text ?? '';
      caption.dataset.visible = text === null || text === '' ? 'false' : 'true';
    },
  };
}
